/**
 * HelpDrawer (P2.1-S6, modernized Doc 56) — slide-in panel từ phải, mở
 * qua "?" topbar hoặc `Cmd+/` global shortcut.
 *
 * 5 sections (accordion):
 *  1. Phím tắt — keyboard shortcuts
 *  2. Tính năng chính — user-facing features
 *  3. User actions — auth/profile/sessions self-service (Doc 47-55)
 *  4. Admin actions — invite, reset, sessions, cleanup (Doc 50-53)
 *  5. FAQ — câu hỏi thường gặp
 *  6. Liên hệ — version + admin email
 */
import {
  Bell,
  Cpu,
  FileText,
  KeyRound,
  Mail,
  MessageCircle,
  Mic,
  Monitor,
  Settings,
  ShieldOff,
  Sparkles,
  UserPlus,
  Users,
  Workflow,
  X,
} from "lucide-react";
import * as React from "react";

import { Accordion, AccordionSection } from "@/components/ui/accordion";
import { useOnboardingStore } from "@/stores/onboarding";

export function HelpDrawer(): JSX.Element | null {
  const open = useOnboardingStore((s) => s.helpOpen);
  const closeHelp = useOnboardingStore((s) => s.closeHelp);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") closeHelp();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, closeHelp]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[55] bg-black/30 backdrop-blur-[2px] animate-fade-in"
        onClick={closeHelp}
        aria-hidden
      />
      <aside
        className="fixed right-0 top-0 bottom-0 z-[56] w-full sm:w-96 bg-background border-l shadow-2xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
        aria-label="Help & shortcuts"
      >
        <header className="flex items-center justify-between px-4 py-3 border-b bg-gradient-mesh shrink-0">
          <h2 className="text-sm font-semibold">Trợ giúp</h2>
          <button
            type="button"
            onClick={closeHelp}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Đóng"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          <Accordion>
            <AccordionSection
              id="shortcuts"
              title="Phím tắt"
              subtitle="6 lệnh nhanh"
              defaultOpen
            >
              <ShortcutsList />
            </AccordionSection>

            <AccordionSection
              id="features"
              title="Tính năng chính"
              subtitle="quick overview"
            >
              <FeaturesList />
            </AccordionSection>

            <AccordionSection
              id="user-actions"
              title="User actions"
              subtitle="self-service tài khoản"
            >
              <UserActionsList />
            </AccordionSection>

            <AccordionSection
              id="admin-actions"
              title="Admin actions"
              subtitle="quản lý hệ thống"
            >
              <AdminActionsList />
            </AccordionSection>

            <AccordionSection
              id="faq"
              title="FAQ"
              subtitle="câu hỏi thường gặp"
            >
              <FAQList />
            </AccordionSection>

            <AccordionSection
              id="contact"
              title="Liên hệ"
              subtitle="version + repo"
            >
              <ContactInfo />
            </AccordionSection>
          </Accordion>
        </div>
      </aside>
    </>
  );
}

function ShortcutsList(): JSX.Element {
  const items = [
    {
      keys: ["⌘", "K"],
      label: "Command Palette",
      hint: "Mở quick-jump tới mọi page",
    },
    {
      keys: ["⌘", "/"],
      label: "Toggle Help drawer",
      hint: "Mở/đóng panel này",
    },
    {
      keys: ["⌘", "S"],
      label: "Save Draft",
      hint: "Trong Form Builder + Workflow Designer",
    },
    {
      keys: ["Del"],
      label: "Xoá field/node selected",
      hint: "Trong Form Builder + Workflow Designer",
    },
    {
      keys: ["⌘", "D"],
      label: "Duplicate field",
      hint: "Chỉ Form Builder",
    },
    {
      keys: ["Esc"],
      label: "Đóng modal/palette/drawer",
    },
  ];
  return (
    <ul className="space-y-1.5">
      {items.map((it) => (
        <li
          key={it.label}
          className="flex items-start gap-2 text-xs"
        >
          <div className="flex items-center gap-1 shrink-0">
            {it.keys.map((k, i) => (
              <kbd
                key={i}
                className="inline-flex items-center justify-center min-w-[20px] px-1 border rounded text-[10px] bg-muted"
              >
                {k}
              </kbd>
            ))}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium">{it.label}</div>
            {it.hint ? (
              <div className="text-[10px] text-muted-foreground">{it.hint}</div>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function FeaturesList(): JSX.Element {
  const items: Array<{
    Icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
  }> = [
    {
      Icon: MessageCircle,
      title: "Chat ERP với AI",
      description:
        "Hỏi tự nhiên, AI tra Odoo. Khi cần ghi dữ liệu, AI render form widget để bạn confirm.",
    },
    {
      Icon: Mic,
      title: "Voice MVP",
      description:
        "Mic button trong composer — push-to-talk → Whisper STT → text auto-fill.",
    },
    {
      Icon: Sparkles,
      title: "AI Insights tuần",
      description:
        "Mỗi Chủ Nhật AI tự tổng kết hoạt động tuần qua. Vào /me/insights để xem.",
    },
    {
      Icon: Cpu,
      title: "Local LLM (Ollama)",
      description:
        "On-prem inference miễn phí. Admin bật on-prem mode → mọi chat qua Ollama, không gọi cloud.",
    },
    {
      Icon: FileText,
      title: "Form Builder",
      description:
        "Tạo form với 14 field types + binding tới Odoo model. Versioning + 4-layer override.",
    },
    {
      Icon: Workflow,
      title: "Workflow Designer",
      description:
        "Visual flow chart 11 node types: trigger → llm/tool/form → branch/parallel → human approval → notify.",
    },
    {
      Icon: Bell,
      title: "Notifications",
      description:
        "Email / Zalo / Teams / Slack / SMS. Rule-based với template engine. WS push real-time.",
    },
    {
      Icon: Settings,
      title: "Settings & Secrets",
      description:
        "Tenant settings + per-user overrides + encrypted API key vault.",
    },
  ];
  return (
    <ul className="space-y-2.5">
      {items.map(({ Icon, title, description }) => (
        <li key={title} className="flex gap-2.5 text-xs">
          <Icon className="size-4 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-medium">{title}</div>
            <div className="text-muted-foreground leading-relaxed">{description}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function UserActionsList(): JSX.Element {
  const items: Array<{
    Icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
    where: string;
  }> = [
    {
      Icon: KeyRound,
      title: "Đổi mật khẩu",
      description:
        "Native: verify pwd cũ → set pwd mới. Nếu chỉ có Odoo identity → /set-password tạo native trước.",
      where: "Settings → Đổi mật khẩu",
    },
    {
      Icon: Monitor,
      title: "Xem & revoke phiên đăng nhập",
      description:
        "Liệt kê devices đang login (IP, user-agent). Revoke session lạ ngay nếu nghi compromise.",
      where: "Settings → Phiên đăng nhập",
    },
    {
      Icon: Mail,
      title: "Quên mật khẩu",
      description:
        "Tại login form click 'Quên mật khẩu?' → nhập email → liên hệ admin nhận URL setup mới.",
      where: "Login → Quên mật khẩu?",
    },
    {
      Icon: ShieldOff,
      title: "Deactivate tài khoản",
      description:
        "Khoá account (sessions revoked). Không hard delete — admin có thể reactivate sau.",
      where: "Settings → Tài khoản",
    },
  ];
  return (
    <ul className="space-y-2.5">
      {items.map(({ Icon, title, description, where }) => (
        <li key={title} className="flex gap-2.5 text-xs">
          <Icon className="size-4 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-medium">{title}</div>
            <div className="text-muted-foreground leading-relaxed">
              {description}
            </div>
            <div className="text-[10px] text-muted-foreground italic mt-0.5">
              📍 {where}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function AdminActionsList(): JSX.Element {
  const items: Array<{
    Icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
    where: string;
  }> = [
    {
      Icon: UserPlus,
      title: "Invite user mới",
      description:
        "Sinh setup token + URL. Share URL với user qua DM, họ tự set password lần đầu.",
      where: "Admin → Users → Invite",
    },
    {
      Icon: Mail,
      title: "Generate reset link",
      description:
        "User quên pwd → click 'Reset link' → URL auto-copy clipboard, share với họ.",
      where: "Admin → Users → row Reset link",
    },
    {
      Icon: Users,
      title: "Map Odoo identity",
      description:
        "User cần dùng ERP tools (search_partner...) phải link Odoo uid + db.",
      where: "Admin → Users → Odoo button",
    },
    {
      Icon: Cpu,
      title: "Bật on-prem mode (Ollama)",
      description:
        "Tenant flag — mọi chat qua Ollama, reject cloud LLM. Cho khách nhạy cảm dữ liệu.",
      where: "Admin → Local LLM",
    },
    {
      Icon: Monitor,
      title: "Sessions force-revoke",
      description:
        "Xem all sessions của user khác + revoke bất kỳ session nghi ngờ.",
      where: "Admin → Users → /sessions endpoint",
    },
    {
      Icon: Settings,
      title: "Cleanup hygiene",
      description:
        "Beat tasks tự xoá expired sessions + archived conversations. Admin xem status hoặc trigger thủ công.",
      where: "API /admin/cleanup/status",
    },
  ];
  return (
    <ul className="space-y-2.5">
      {items.map(({ Icon, title, description, where }) => (
        <li key={title} className="flex gap-2.5 text-xs">
          <Icon className="size-4 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-medium">{title}</div>
            <div className="text-muted-foreground leading-relaxed">
              {description}
            </div>
            <div className="text-[10px] text-muted-foreground italic mt-0.5">
              📍 {where}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function FAQList(): JSX.Element {
  const faqs = [
    {
      q: "Em có tự ghi dữ liệu vào Odoo không?",
      a: "Không. Em luôn render form widget để anh review + confirm trước khi ghi. Mọi thao tác ghi đều qua tool có audit log.",
    },
    {
      q: "Em hỗ trợ provider AI nào?",
      a: "Anthropic Claude, OpenAI GPT, Google Gemini, DeepSeek, và Ollama (local, miễn phí). Đổi qua Settings → Integrations.",
    },
    {
      q: "Cài API key ở đâu?",
      a: "Vào Settings → API Keys (chỉ admin). Key được mã hoá lưu DB, không log raw.",
    },
    {
      q: "Đăng ký tài khoản mới như thế nào?",
      a: "Trang login → tab 'Đăng ký' → email + password. User đầu tiên tự động được role admin (bootstrap). Hoặc admin invite qua /admin/users.",
    },
    {
      q: "Tại sao chat báo 'cần Odoo identity'?",
      a: "User chưa link Odoo uid. Native chat AI vẫn chạy nhưng tools ERP (search_partner, create_quotation...) bị filter. Liên hệ admin map.",
    },
    {
      q: "Quên mật khẩu thì làm sao?",
      a: "Login form → 'Quên mật khẩu?' → nhập email. Hệ thống tạo reset token (audit log), admin xem + share URL với bạn. v1.1 sẽ tự gửi email.",
    },
    {
      q: "Có thể logout từ device khác không?",
      a: "Có. Settings → Phiên đăng nhập → revoke session lẻ. Hoặc /api/v1/auth/logout-all revoke tất cả.",
    },
    {
      q: "Form làm sao bind vào Odoo field?",
      a: "Trong Form Builder → chọn field → properties Advanced → Binding ERP model + label_field.",
    },
    {
      q: "Có thể export audit log không?",
      a: "Admin → Audit Logs → filter theo ngày/user/action → Export CSV.",
    },
    {
      q: "Insights tuần là gì?",
      a: "Mỗi Chủ Nhật AI tự tổng kết các chat trong tuần qua thành 200 từ. Vào /me/insights để xem hoặc generate-now thủ công.",
    },
  ];
  return (
    <ul className="space-y-2 text-xs">
      {faqs.map((f) => (
        <li key={f.q} className="rounded-md border bg-background p-2">
          <div className="font-medium mb-0.5">{f.q}</div>
          <div className="text-muted-foreground leading-relaxed">{f.a}</div>
        </li>
      ))}
    </ul>
  );
}

function ContactInfo(): JSX.Element {
  return (
    <div className="space-y-2 text-xs">
      <div className="flex justify-between">
        <span className="text-muted-foreground">Phiên bản</span>
        <code className="text-foreground">v2.2.0</code>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">Build</span>
        <code className="text-[10px]">{import.meta.env.MODE}</code>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">Domain</span>
        <code className="text-[10px]">agent.ixgroup.vn</code>
      </div>
      <div className="text-[10px] text-muted-foreground space-y-1 mt-3 pt-2 border-t">
        <div>
          <span className="font-medium">Docs included:</span>
        </div>
        <div className="font-mono leading-relaxed">
          P3-S1..S5 · 47 Native auth · 48 Profile · 49 Chat UX · 50 Invite ·
          51 Rate limit · 52 Sessions · 53 Cleanup · 54 Forgot pwd ·
          55 Deactivate · 56 Help (this)
        </div>
      </div>
      <p className="text-muted-foreground italic mt-2">
        Có vấn đề? Liên hệ admin hoặc tạo issue trên repo.
      </p>
    </div>
  );
}
